/**
 * Complete Batch Modal Component
 * Completes a production batch with output quantity
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 *
 * @description
 * Modal component for completing production batches with:
 * - Batch information display (code, input quantity)
 * - Output quantity input with validation
 * - Efficiency calculation (output/input * 100)
 * - Notes textarea for completion remarks
 * - Submit with loading state
 *
 * @example
 * ```tsx
 * const [batch, setBatch] = useState<ProductionBatchDTO | null>(null);
 * const [open, setOpen] = useState(false);
 *
 * <CompleteBatchModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   batch={batch}
 *   onSuccess={(completedBatch) => console.log('Batch completed:', completedBatch)}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { Loader2, Package, TrendingUp, AlertCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';

import { useCompleteProductionBatch } from '@/hooks/useProductionBatches';
import { ProductionBatchDTO } from '@/services/manufacturing/manufacturingApi';
import { toast } from 'sonner';

interface CompleteBatchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatchDTO | null;
  onSuccess?: (batch: ProductionBatchDTO) => void;
}

/**
 * Complete Batch Modal Component
 */
export function CompleteBatchModal({
  open,
  onOpenChange,
  batch,
  onSuccess,
}: CompleteBatchModalProps) {
  const [outputQuantityKg, setOutputQuantityKg] = useState<string>('');
  const [notes, setNotes] = useState('');

  // Complete batch mutation
  const completeBatch = useCompleteProductionBatch();

  const inputQty = batch ? Number(batch.totalInputQuantityKg) : 0;
  const parsedOutputQty = outputQuantityKg === '' ? NaN : parseFloat(outputQuantityKg);
  const isOutputExceedingInput =
    !isNaN(parsedOutputQty) && inputQty > 0 && parsedOutputQty > inputQty;

  // Calculate efficiency
  const efficiency = batch && outputQuantityKg && inputQty > 0
    ? (parsedOutputQty / inputQty) * 100
    : 0;

  // Reset form when batch changes
  useEffect(() => {
    if (batch) {
      setOutputQuantityKg('');
      setNotes('');
    }
  }, [batch]);

  // Handle submit
  const handleSubmit = async () => {
    if (!batch) return;

    const outputQty = parseFloat(outputQuantityKg);

    if (isNaN(outputQty) || outputQty <= 0) {
      toast.error('Please enter a valid output quantity');
      return;
    }

    // Hard block: output quantity cannot exceed input quantity
    if (outputQty > inputQty) {
      toast.error(
        `Output quantity (${outputQty} kg) cannot exceed input quantity (${inputQty.toFixed(
          2
        )} kg). Please enter a value less than or equal to the input quantity.`
      );
      return;
    }

    // Soft warn if efficiency is unusually low
    if (efficiency < 50) {
      if (
        !confirm(
          `The efficiency is ${efficiency.toFixed(2)}%, which is quite low. Are you sure?`
        )
      ) {
        return;
      }
    }

    try {
      const result = await completeBatch.mutateAsync({
        batchId: batch.id,
        data: {
          outputQuantityKg: outputQty,
          notes: notes || undefined,
        },
      });

      toast.success(`Production batch ${batch.batchCode} completed successfully`);

      onSuccess?.(result);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete production batch');
    }
  };

  // Get efficiency color
  const getEfficiencyColor = () => {
    if (efficiency >= 90) return 'text-green-600 dark:text-green-400';
    if (efficiency >= 70) return 'text-blue-600 dark:text-blue-400';
    if (efficiency >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Get efficiency progress color
  const getEfficiencyProgressColor = () => {
    if (efficiency >= 90) return 'bg-green-500';
    if (efficiency >= 70) return 'bg-blue-500';
    if (efficiency >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Complete Production Batch</DialogTitle>
          <DialogDescription>
            Enter the output quantity to complete batch {batch?.batchCode}
          </DialogDescription>
        </DialogHeader>

        {batch && (
          <div className="space-y-4 py-4">
            {/* Batch Information */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Batch Code:</span>
                    <p className="font-medium">{batch.batchCode}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dung Product:</span>
                    <p className="font-medium">{batch.dungProductName}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Manufacturing Unit:</span>
                    <p className="font-medium">{batch.manufacturingUnitName}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      Total Input Quantity:
                    </span>
                    <p className="font-medium text-lg">
                      {batch.totalInputQuantityKg.toFixed(2)} kg
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Output Quantity Input */}
            <div className="space-y-2">
              <Label htmlFor="output-quantity">
                Output Quantity (kg) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="output-quantity"
                type="text"
                inputMode="decimal"
                placeholder={`Enter output quantity (max ${inputQty.toFixed(2)} kg)`}
                value={outputQuantityKg}
                onChange={(e) => {
                  const rawValue = e.target.value;

                  if (rawValue === '') {
                    setOutputQuantityKg('');
                    return;
                  }

                  // Allow digits with at most one decimal point and up to 3 decimal places
                  if (!/^\d*\.?\d{0,3}$/.test(rawValue)) {
                    return;
                  }

                  // Clamp to input quantity (output cannot exceed input)
                  const numValue = parseFloat(rawValue);
                  if (!isNaN(numValue) && numValue > inputQty) {
                    setOutputQuantityKg(String(inputQty));
                    toast.error(
                      `Output quantity cannot exceed input quantity (${inputQty.toFixed(
                        2
                      )} kg)`
                    );
                    return;
                  }

                  setOutputQuantityKg(rawValue);
                }}
                aria-invalid={isOutputExceedingInput}
                required
              />
              <p className="text-xs text-muted-foreground">
                Input quantity: {inputQty.toFixed(2)} kg. Output must be less than
                or equal to input.
              </p>
            </div>

            {/* Efficiency Calculation */}
            {outputQuantityKg && efficiency > 0 && (
              <Card className="border-blue-200 dark:border-blue-800">
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Production Efficiency
                      </span>
                      <span className={`text-xl font-bold ${getEfficiencyColor()}`}>
                        {efficiency.toFixed(2)}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(efficiency, 100)}
                      className="h-2"
                      indicatorClassName={getEfficiencyProgressColor()}
                    />
                    <p className="text-xs text-muted-foreground">
                      {efficiency > 100 && (
                        <span className="text-orange-600 dark:text-orange-400">
                          Output exceeds input (over 100%)
                        </span>
                      )}
                      {efficiency >= 90 && efficiency <= 100 && (
                        <span className="text-green-600 dark:text-green-400">
                          Excellent efficiency
                        </span>
                      )}
                      {efficiency >= 70 && efficiency < 90 && (
                        <span className="text-blue-600 dark:text-blue-400">
                          Good efficiency
                        </span>
                      )}
                      {efficiency >= 50 && efficiency < 70 && (
                        <span className="text-yellow-600 dark:text-yellow-400">
                          Moderate efficiency
                        </span>
                      )}
                      {efficiency < 50 && (
                        <span className="text-red-600 dark:text-red-400">
                          Low efficiency - please verify
                        </span>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Error for output exceeding input */}
            {isOutputExceedingInput && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Output quantity ({parsedOutputQty} kg) cannot exceed input
                  quantity ({inputQty.toFixed(2)} kg). Please enter a value less
                  than or equal to the input quantity.
                </AlertDescription>
              </Alert>
            )}

            {/* Warning for unusually low efficiency */}
            {outputQuantityKg && !isOutputExceedingInput && efficiency > 0 && efficiency < 50 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  The efficiency is unusually low. Please verify the measurements.
                </AlertDescription>
              </Alert>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Completion Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about the production batch completion..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              completeBatch.isPending ||
              !outputQuantityKg ||
              parseFloat(outputQuantityKg) <= 0 ||
              isOutputExceedingInput
            }
          >
            {completeBatch.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Complete Batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompleteBatchModal;
